import { CommonErrorCodeEnum } from "jadro-pico/enums/common/CommonErrorCodeEnum.ts";
import { refreshToken } from "./tokenRefresh.ts";

export interface ApiFetchOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  timeout?: number;
  allowAnonymous?: boolean;
  skipAuthRefresh?: boolean;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly errorCode: string,
    public readonly data?: unknown,
  ) {
    super(`${status}: ${errorCode}`);
  }
}

const DEFAULT_TIMEOUT_MS = 30_000;

let onAuthFailureRedirect: (() => void) | null = null;

export function configureAuthFailureRedirect(handler: () => void): void {
  onAuthFailureRedirect = handler;
}

async function doFetch(url: string, options: ApiFetchOptions): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, options.timeout ?? DEFAULT_TIMEOUT_MS);
  const linkedSignal = options.signal
    ? AbortSignal.any([options.signal, controller.signal])
    : controller.signal;

  try {
    const init: RequestInit = {
      method: options.method ?? "GET",
      credentials: "include",
      signal: linkedSignal,
    };
    if (options.body !== undefined) {
      init.headers = { "Content-Type": "application/json" };
      init.body = JSON.stringify(options.body);
    }
    return await fetch(url, init);
  } finally {
    clearTimeout(timeoutId);
  }
}

interface ErrorPayload {
  errorCode?: string;
  data?: unknown;
}

async function readErrorPayload(response: Response): Promise<ErrorPayload> {
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) return {};
  return ((await response.json().catch(() => null)) as ErrorPayload | null) ?? {};
}

export async function apiFetch<T = unknown>(
  url: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  let response = await doFetch(url, options);

  if (response.status === 401) {
    const originalPayload = await readErrorPayload(response.clone());
    const originalErrorCode = originalPayload.errorCode ?? CommonErrorCodeEnum.UNAUTHORIZED;

    if (options.skipAuthRefresh) {
      throw new ApiError(401, originalErrorCode, originalPayload.data);
    }

    try {
      await refreshToken();
      response = await doFetch(url, options);
    } catch {
      if (options.allowAnonymous) return null as T;
      onAuthFailureRedirect?.();
      throw new ApiError(401, originalErrorCode, originalPayload.data);
    }
    if (response.status === 401) {
      if (options.allowAnonymous) return null as T;
      const retryPayload = await readErrorPayload(response.clone());
      onAuthFailureRedirect?.();
      throw new ApiError(
        401,
        retryPayload.errorCode ?? CommonErrorCodeEnum.UNAUTHORIZED,
        retryPayload.data,
      );
    }
  }

  const contentType = response.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : null;

  if (!response.ok) {
    const errorCode =
      (payload as ErrorPayload | null)?.errorCode ?? CommonErrorCodeEnum.INTERNAL_SERVER_ERROR;
    throw new ApiError(response.status, errorCode, (payload as ErrorPayload | null)?.data);
  }

  return payload as T;
}
